import { type FC, useContext, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import DatePicker from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import './styles/styles.css';

import { ErrorMessage } from '@hookform/error-message';
import type { AxiosError } from 'axios';
import { DateTime } from 'luxon';
import { Col, Form, Row } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import {
  type ErrorEnvelop,
  useAuth,
  useMutationRequest,
  useNotifications,
} from '../../../modules/hooks';
import type { GuestPayload, Guests } from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import { getApiErrorMessage } from '../../../modules/utils/transform';

type EditGuestProp = {
  show: boolean;
  guestData?: Guests;
  minimalAge?: number;
  onHide: () => void;
  updatedGuestHandler: () => void;
};

const EditGuestModal: FC<EditGuestProp> = ({
  show,
  guestData,
  minimalAge,
  onHide,
  updatedGuestHandler,
}) => {
  const { credentialsInfo } = useAuth()!;
  const { setSimpleToasts } = useNotifications()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const {
    register,
    handleSubmit,

    trigger,
    reset,
    control,

    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      email: '',
      line1: '',
      city: '',
      postalCode: '',
      birthDate: '',
      state: '',
      countryCode: '',
      phoneNumber: '',
    },
  });

  const editUrl = `${endpoints.GUESTS}/${guestData?.uuid}`;
  const [
    { loading: updateLoading, data: updateResult, error: updateError },
    updateGuest,
  ] = useMutationRequest<string>(
    editUrl,
    'patch',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property.uuid,
    },
    { manual: true },
  );

  const updateGuestHandler = (data: GuestPayload) => {
    // this name logic needs to be refactored again, this is a quick fix
    const nameString = data.name.trim();
    const nameSplit = nameString.split(' ');
    const [namePart1 = '', namePart2 = '', namePart3 = '', namePart4 = ''] = nameSplit;
    const firstName =
      nameSplit.length === 2 || nameSplit.length === 1
        ? namePart1
        : `${namePart1} ${namePart2}`.trim();
    const lastName =
      nameSplit.length === 2 ? namePart2 : `${namePart3} ${namePart4}`.trim();

    const dateTime = DateTime.fromISO(data?.birthDate || '', { zone: 'utc' });
    const birthDate = dateTime.isValid ? dateTime.toJSDate() : undefined;

    updateGuest({
      firstName,
      lastName,
      city: data.city,
      line1: data.line1,
      email: data.email,
      phoneNumber: data.phoneNumber,
      zipCode: data.postalCode,
      birthDate,
      countryCode: data.countryCode,
      state: data.state,
    });
  };

  const onSubmit = async (data: GuestPayload) => {
    const validated = await trigger([
      'name',
      'email',
      'line1',
      'city',
      'postalCode',
      'birthDate',
      'phoneNumber',
      'state',
      'countryCode',
    ]);

    if (validated) {
      updateGuestHandler(data);
    }
  };
  const onCancel = async () => {
    onHide();
    reset({
      name: '',
      email: '',
      line1: '',
      city: '',
      postalCode: '',
      birthDate: '',
      phoneNumber: '',
      countryCode: '',
      state: '',
    });
  };

  useEffect(() => {
    if (!guestData || !show) return;

    const name = `${guestData?.firstName} ${guestData?.lastName}`;

    const birthDate = guestData?.birthDate
      ? `${DateTime.fromISO(guestData?.birthDate, { zone: 'utc' }).toFormat(
          'MM/dd/yyyy',
        )}`
      : '';

    reset({
      name,
      email: guestData.email || '',
      line1: guestData.line1 || '',
      city: guestData.city || '',
      postalCode: guestData.zipCode || '',
      birthDate,
      phoneNumber: guestData.phoneNumber || '',
      state: guestData.state || '',
      countryCode: guestData.countryCode || '',
    });
  }, [guestData, show]);

  useEffect(() => {
    if (updateResult) {
      updatedGuestHandler();
    }

    if (updateError && updateError?.message) {
      const message = getApiErrorMessage(updateError as AxiosError<ErrorEnvelop>);

      setSimpleToasts({
        message,
        type: 'danger',
        show: true,
      });
    }
  }, [updateResult, updateError]);

  return (
    <Modal
      className="modal-view-style"
      show={show}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      id="edit-guest-modal"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Header>
          <Modal.Title className="edit-guest-modal-title">
            Edit guest information
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col>
              <Form.Label className="editor-title-label">Name</Form.Label>

              <Form.Control
                className="edit-guest-input-style "
                placeholder="Enter name"
                {...register('name', { required: 'This is required.' })}
              />
              <ErrorMessage
                errors={errors}
                name="name"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">E-mail</Form.Label>

              <Form.Control
                className="edit-guest-input-style "
                placeholder="Enter email"
                {...register('email', {
                  required: 'This is required.',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Entered value does not match email format',
                  },
                })}
              />
              <ErrorMessage
                errors={errors}
                name="email"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Street</Form.Label>

              <Form.Control
                className="edit-guest-input-style "
                placeholder="Enter address line 1"
                {...register('line1')}
              />
              <ErrorMessage
                errors={errors}
                name="line1"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">City</Form.Label>

              <Form.Control
                className="edit-guest-input-style "
                placeholder="Enter city"
                {...register('city')}
              />
              <ErrorMessage
                errors={errors}
                name="city"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">State</Form.Label>

              <Form.Control
                className="edit-guest-input-style "
                placeholder="Enter state"
                {...register('state')}
              />
              <ErrorMessage
                errors={errors}
                name="state"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Zip Code</Form.Label>

              <Form.Control
                className="edit-guest-input-style "
                placeholder="Enter the zip code"
                {...register('postalCode')}
              />
              <ErrorMessage
                errors={errors}
                name="postalCode"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Country Code</Form.Label>

              <Form.Control
                className="edit-guest-input-style "
                placeholder="Enter country code"
                {...register('countryCode')}
              />
              <ErrorMessage
                errors={errors}
                name="countryCode"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>

          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Date of Birth</Form.Label>

              <Controller
                control={control}
                name="birthDate"
                rules={{
                  validate: (value) => {
                    if (!value) return true;

                    const birthDate = new Date(value);
                    const today = new Date();
                    const age = today.getFullYear() - birthDate.getFullYear();
                    const validAge = minimalAge || 21;
                    const monthDiff = today.getMonth() - birthDate.getMonth();
                    const dayDiff = today.getDate() - birthDate.getDate();

                    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
                      return age - 1 >= validAge
                        ? true
                        : `You must be at least ${validAge || 21} years old.`;
                    }

                    return age >= validAge
                      ? true
                      : `You must be at least ${validAge || 21} years old.`;
                  },
                }}
                render={({ field }) => (
                  <DatePicker
                    wrapperClassName="datePicker"
                    placeholderText="Select date"
                    selected={field.value ? new Date(field.value) : null}
                    onChange={(date) => {
                      if (date) {
                        const stringDate = date.toISOString();
                        field.onChange(stringDate);
                      } else {
                        field.onChange(undefined);
                      }
                    }}
                    customInput={
                      <Form.Control className="edit-guest-input-style" inputMode="none" />
                    }
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    maxDate={new Date()}
                    dateFormat="MM/dd/yyyy"
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="birthDate"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>

          <Row className="mt-16px">
            <Col>
              <Form.Label className="editor-title-label">Phone number</Form.Label>

              <Controller
                name="phoneNumber"
                control={control}
                rules={{
                  validate: {
                    hasMoreThanFourDigits: (value) => {
                      const digits = value.replace(/\D/g, '');
                      return digits.length > 4 || 'Enter a valid number.';
                    },
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <PhoneInput
                    country="us"
                    value={value}
                    onChange={(phoneValue) => {
                      onChange(
                        phoneValue.startsWith('+') ? phoneValue : `+${phoneValue}`,
                      );
                    }}
                    inputStyle={{ width: '100%' }}
                    countryCodeEditable={false}
                  />
                )}
              />

              <ErrorMessage
                errors={errors}
                name="phoneNumber"
                render={({ message }) => <span className="error-text">{message}</span>}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className="edit-guest-modal-button"
            onClick={onCancel}
            disabled={updateLoading}
          >
            Cancel
          </Button>
          <Button
            className="edit-guest-modal-button-save"
            type="submit"
            disabled={updateLoading}
          >
            {updateLoading ? 'Loading…' : 'Save'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EditGuestModal;
